import { useState } from "react";
import { toast } from "sonner";
import { Panel, Chip, Th, Td, Kpi } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EquityCurve } from "@/components/EquityCurve";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, fmt, gbp, pnlClass, ldnTime, cur } from "@/lib/api";

export function OilHistory() {
  const { data: trades } = usePoll("/oil/trades", 20000);
  const { data: log } = usePoll("/oil/log", 15000);
  return (
    <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
      <Panel title="Trade history" testid="oil-trade-history">
        {!trades?.length ? <p className="text-sm text-slate-500">No closed spread trades yet.</p> : (
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full min-w-[760px]">
              <thead className="border-b border-slate-800"><tr><Th>Opened</Th><Th>Closed</Th><Th>Side</Th><Th>Legs</Th><Th>Entry</Th><Th>Exit</Th><Th>Points</Th><Th>{cur()}</Th><Th>Reason</Th></tr></thead>
              <tbody>{trades.map((t) => (
                <tr key={t.id} data-testid={`oil-trade-row-${t.id}`} className="border-b border-slate-800/60">
                  <Td className="text-xs text-slate-400">{ldnTime(t.opened_at)}</Td><Td className="text-xs text-slate-400">{ldnTime(t.closed_at)}</Td>
                  <Td><Chip status={t.dir > 0 ? "LONG" : "SHORT"} label={t.side} /></Td>
                  <Td className="num text-xs">{t.brent_symbol}/{t.wti_symbol} {cur()}{fmt(t.stake)}</Td>
                  <Td className="num">{fmt(t.entry_spread, 3)}</Td><Td className="num">{fmt(t.exit_spread, 3)}</Td>
                  <Td className={`num ${pnlClass(t.points)}`}>{fmt(t.points)}</Td><Td className={`num font-semibold ${pnlClass(t.pnl)}`}>{gbp(t.pnl)}</Td>
                  <Td className="num text-xs">{t.reason}</Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel title="Activity log" testid="oil-activity-log">
        <div className="max-h-[420px] space-y-1.5 overflow-auto">
          {!log?.length && <p className="text-sm text-slate-500">Nothing logged yet.</p>}
          {(log || []).map((l) => (
            <div key={l.id} className="flex gap-3 border-b border-slate-800/50 pb-1.5 text-xs">
              <span className="num shrink-0 text-slate-500">{ldnTime(l.at)}</span>
              <span className={l.kind === "leg_mismatch" ? "text-rose-300" : "text-slate-300"}>{l.msg}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

export function Replay() {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ start: new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10), end: today });
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/oil/replay", f); setR(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Replay over recorded prices" testid="oil-replay">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-400">From<Input type="date" data-testid="replay-start-input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1 h-9 bg-slate-950" /></label>
        <label className="text-xs text-slate-400">To<Input type="date" data-testid="replay-end-input" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1 h-9 bg-slate-950" /></label>
        <Button data-testid="replay-run-button" variant="secondary" disabled={busy} onClick={run}>{busy ? "Running" : "Run replay"}</Button>
      </div>
      {r && (
        <div className="mt-4 space-y-4" data-testid="replay-results">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Samples / bars" value={`${r.samples} / ${r.bars}`} />
            <Kpi label="Trades" value={r.trades.length} testid="replay-trades-count" />
            <Kpi label="Win rate" value={r.win_rate == null ? "-" : `${fmt(r.win_rate, 1)}%`} />
            <Kpi label="Net" value={gbp(r.net_gbp)} className={pnlClass(r.net_gbp)} testid="replay-net" />
            <Kpi label="Max drawdown" value={gbp(r.max_drawdown_gbp)} className="text-rose-300" />
          </div>
          <EquityCurve points={[{ t: null, equity: 0 }, ...r.equity_curve]} title={`Replay equity (${cur()} from zero)`} testid="replay-equity" />
        </div>
      )}
    </Panel>
  );
}

export function Maintenance({ s, onChange }) {
  const [stray, setStray] = useState(null);
  const call = async (path, body, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return null;
    try { const { data } = await api.post(path, body); if (msg) toast.success(msg); onChange(); return data; } catch (e) { toast.error(errMsg(e)); return null; }
  };
  if (!s.is_admin) return null;
  const t = s.open_trades?.[0];
  return (
    <Panel title="Maintenance" testid="oil-maintenance">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-200">Stuck trade</h3>
          <p className="text-xs text-slate-500">Checks the open trade's live legs against MT5 and marks legs that are gone as closed.</p>
          <Button data-testid="oil-reconcile-button" size="sm" variant="secondary" disabled={!t} onClick={async () => { const d = await call(`/oil/maintenance/reconcile/${t.id}`, {}, null); if (d) toast.info(`Brent ${d.report.brent}, WTI ${d.report.wti}`); }}>Reconcile open trade</Button>
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-200">Stray positions</h3>
          <p className="text-xs text-slate-500">Finds Oil Pairs positions on MT5 that no open trade owns. Dry run first.</p>
          <div className="flex gap-2">
            <Button data-testid="oil-stray-dry-run" size="sm" variant="secondary" onClick={async () => setStray(await call("/oil/maintenance/stray-cleanup", { execute: false }))}>Dry run</Button>
            <Button data-testid="oil-stray-execute" size="sm" variant="destructive" disabled={!stray?.strays?.length} onClick={async () => setStray(await call("/oil/maintenance/stray-cleanup", { execute: true }, "Strays closed", "Close all stray positions on MT5?"))}>Execute</Button>
          </div>
          {stray && <div className="num text-xs text-slate-400" data-testid="oil-stray-result">{stray.strays.length} stray position(s){stray.strays.map((p) => ` ${p.symbol} ${p.volume}`).join(",")}</div>}
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-200">Reset</h3>
          <p className="text-xs text-slate-500">Wipes paper trades, the activity log and the gate state. Recorded prices are kept for replay.</p>
          <Button data-testid="oil-reset-button" size="sm" variant="destructive" onClick={() => call("/oil/maintenance/reset", {}, "Oil Pairs reset", "Wipe Oil Pairs paper trades and log?")}>Reset Oil Pairs</Button>
        </div>
      </div>
    </Panel>
  );
}
